import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { colors } from '../../../theme/colors';
import { body, headers, richTextStyles } from '../../../styles/type';
import { fontWeights } from '../../../theme/type';
import { rem } from '../../../utilities/style';
import { media } from '../../../theme/media';

import Button from '../../buttons/Button';
import ButtonGroup from '../../ButtonGroup';
import ButtonText from '../../buttons/ButtonText';
import TextRenderer from '../../TextRenderer';

const Container = styled.div`
  border: 1px solid ${({ borderColor }) => borderColor};
  border-radius: 10px;
  padding: ${rem(24)};
  color: ${colors.gray['600']};

  ${media('>smedium')} {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${rem(24)};
    padding: ${rem(48)};
  }
`;

const TopContent = styled.div`
  padding-bottom: ${rem(24)};
`;

const Overview = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h3`
  ${headers.h3}
  color: ${colors.blue['800']};
`;

const Pill = styled.span`
  font-size: ${rem(12)};
  font-weight: ${fontWeights.semiBold};
  background-color: ${colors.blue['100']};
  color: ${colors.blue['800']};
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${rem(100)};
`;

const PriceWrapper = styled.div`
  margin: 24px 0;
`;

const DurationText = styled.span`
  font-size: ${rem(16)};
  font-weight: ${fontWeights.regular};
  line-height: 1;
  color: ${colors.gray['600']};
  text-wrap: nowrap;
`;

const OriginalPrice = styled.p`
  text-decoration: line-through;
`;

const PriceAfterText = styled.p`
  ${body.regular}
  font-weight: ${fontWeights.regular};
  margin-top: ${rem(8)};
  color: ${colors.gray['600']};
`;

const Price = styled.h2`
  padding-top: ${rem(8)};
  font-size: ${rem(52)};
  font-weight: ${fontWeights.bold};
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${colors.blue['800']};
  word-wrap: break-word;
`;

const ContentContainer = styled.div`
  padding-top: ${rem(24)};

  ${media('>smedium')} {
    padding-top: ${rem(12)};
  }
`;

const Description = styled.div`
  ${richTextStyles}
`;

const PricingCard = ({
  title,
  price,
  durationText,
  pricingNote,
  originalPriceText,
  featureBadgeText,
  ctaText,
  ctaLink,
  secondaryCtaText,
  secondaryCtaLink,
  description,
  borderColor,
}) => {
  // replace spaces with \xa0 so they display non-breaking spaces. "nbsp"
  const handleNonBreaking = (time) => `/\xa0${time.replace(/ /g, '\xa0')}`;

  return (
    <Container borderColor={borderColor}>
      <TopContent>
        <Overview>
          {title && (
            <Title>
              <TextRenderer text={title} />
            </Title>
          )}
          <PriceWrapper>
            {featureBadgeText && (
              <Pill>
                <TextRenderer text={featureBadgeText} />
              </Pill>
            )}
            {price && (
              <Price>
                <TextRenderer text={price} />
                {durationText && (
                  <DurationText>
                    <TextRenderer text={handleNonBreaking(durationText)} />
                  </DurationText>
                )}
              </Price>
            )}
            {originalPriceText && (
              <OriginalPrice>
                <TextRenderer text={originalPriceText} />
              </OriginalPrice>
            )}
            {pricingNote && (
              <PriceAfterText>
                <TextRenderer text={pricingNote} />
              </PriceAfterText>
            )}
          </PriceWrapper>
        </Overview>
        <ButtonGroup>
          {ctaText && ctaLink && (
            <Button to={ctaLink}>
              <TextRenderer text={ctaText} />
            </Button>
          )}
          {secondaryCtaText && secondaryCtaLink && (
            <ButtonText to={secondaryCtaLink}>
              <TextRenderer text={secondaryCtaText} />
            </ButtonText>
          )}
        </ButtonGroup>
      </TopContent>
      {description && (
        <Description>
          <ContentContainer>
            <TextRenderer richText text={description} />
          </ContentContainer>
        </Description>
      )}
    </Container>
  );
};

PricingCard.propTypes = {
  title: PropTypes.string,
  price: PropTypes.string,
  durationText: PropTypes.string,
  pricingNote: PropTypes.string,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  description: PropTypes.shape({
    raw: PropTypes.string,
  }),
  borderColor: PropTypes.string,
};

export default PricingCard;
